import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../../lib/bookingStatus";
import { deDate } from "../../lib/dateFormat";
import CustomerPinPromptDialog from "./CustomerPinPromptDialog";

/**
 * Modell A · Kündigung eines Service-Pakets.
 *  - Kunde: nur EIGENES Paket 1 (PIN-signiert).
 *  - Admin: jedes Paket (keine PIN — Backend erlaubt Admin direkt).
 *
 * Zeigt das frühestmögliche Enddatum (Ende der letzten zukünftigen Abwesenheit)
 * und lässt den Nutzer das Kündigungsdatum wählen. Das Backend erzwingt die
 * Untergrenze (keine ungedeckte Abwesenheit) und liefert das effektive Datum.
 */
export default function ServicePackageCancelDialog({ booking, isAdmin = false, de = true, onClose, onCancelled }) {
  const [info, setInfo] = useState(null);
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/service-packages/${booking.id}/cancel-info`);
        if (!alive) return;
        setInfo(data);
        const preset = data.current_end_date && data.current_end_date >= data.earliest_end_date
          ? data.current_end_date
          : data.earliest_end_date;
        setEndDate(preset || "");
      } catch (e) {
        if (alive) { toast.error(formatErr(e.response?.data?.detail) || e.message); onClose?.(); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [booking.id, onClose]);

  const doCancel = async (pin) => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/service-packages/${booking.id}/cancel`, {
        end_date: endDate || null,
        pin: pin || null,
      });
      toast.success(de
        ? `Paket gekündigt · Betreuung endet am ${deDate(data.effective_end_date)}.`
        : `Package cancelled · care ends on ${deDate(data.effective_end_date)}.`);
      await onCancelled?.(data);
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
      setPinOpen(false);
    }
  };

  const onSubmit = () => {
    if (submitting) return;
    if (isAdmin) doCancel(null);
    else setPinOpen(true);
  };

  const pkgTitle = booking.package_title || `Service-Paket ${booking.package_id}`;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9990] flex items-center justify-center bg-navy/60 backdrop-blur-sm px-4"
        data-testid="service-package-cancel-dialog"
        onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose?.(); }}
      >
        <div className="bg-white border border-navy/15 w-full max-w-md p-7 md:p-8 relative">
          <button
            type="button"
            data-testid="service-package-cancel-close"
            onClick={() => !submitting && onClose?.()}
            className="absolute top-4 right-4 text-navy-dark/50 hover:text-navy transition-colors"
            aria-label={de ? "Schließen" : "Close"}
          >
            <X className="w-5 h-5" strokeWidth={1.6} />
          </button>

          <div className="flex items-center gap-2.5 mb-2">
            <AlertTriangle className="w-5 h-5" style={{ color: STATUS_COLORS.red }} strokeWidth={1.8} />
            <div className="label-caps">{de ? "Paket kündigen" : "Cancel package"}</div>
          </div>
          <h3 className="font-display text-2xl text-navy leading-tight mb-4">{pkgTitle}</h3>

          {loading ? (
            <div className="flex items-center gap-2 text-navy-dark/60 text-[13px] py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> {de ? "Wird geladen …" : "Loading …"}
            </div>
          ) : (
            <>
              {info?.has_future_absence && (
                <div
                  className="mb-5 flex items-start gap-3 border p-4 rounded"
                  style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), borderColor: BRAND_COLORS.gold }}
                  data-testid="service-package-cancel-absence-hint"
                >
                  <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
                  <div className="text-[13px] leading-snug text-navy">
                    {de
                      ? `Für dieses Objekt ist eine Abwesenheit bis ${deDate(info.future_absence_end)} geplant. Die Betreuung kann daher frühestens an diesem Tag enden.`
                      : `An absence is planned for this property until ${deDate(info.future_absence_end)}. Care can therefore end on that day at the earliest.`}
                  </div>
                </div>
              )}

              {info?.change_locked && (
                <div
                  className="mb-5 flex items-start gap-3 border p-4 rounded"
                  style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), borderColor: BRAND_COLORS.gold }}
                  data-testid="service-package-cancel-fee-hint"
                >
                  <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
                  <div className="text-[13px] leading-snug text-navy">
                    {de
                      ? "Weniger als 7 Tage vor Abreise ist keine Änderung mehr möglich, nur eine Stornierung. Dabei können Stornogebühren gemäß unserem Rahmenvertrag anfallen."
                      : "Less than 7 days before departure, no changes are possible — only cancellation. Cancellation fees may apply in accordance with our framework agreement."}
                  </div>
                </div>
              )}

              <div className="mb-6" data-testid="service-package-cancel-enddate-field">
                <label htmlFor="sp-cancel-end" className="label-caps block mb-2">
                  {de ? "Betreuung endet am" : "Care ends on"}
                </label>
                <input
                  id="sp-cancel-end"
                  type="date"
                  min={info?.earliest_end_date || undefined}
                  max={info?.current_end_date || undefined}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="service-package-cancel-end-date"
                  className="w-full md:w-64 border border-navy/20 px-3 py-2.5 text-[15px] text-navy focus:border-gold focus:outline-none"
                />
                <p className="mt-2 text-[12px] text-navy-dark/60">
                  {de
                    ? `Frühestmöglich: ${deDate(info?.earliest_end_date)}${info?.current_end_date ? ` · aktuelles Ende: ${deDate(info.current_end_date)}` : " · Paket 1 läuft fortlaufend"}`
                    : `Earliest possible: ${deDate(info?.earliest_end_date)}${info?.current_end_date ? ` · current end: ${deDate(info.current_end_date)}` : " · Package 1 is ongoing"}`}
                </p>
              </div>

              {!isAdmin && (
                <p className="mb-5 text-[13px] text-navy-dark/70 leading-relaxed" data-testid="service-package-cancel-pin-hint">
                  {de
                    ? "Bestätigen Sie die Kündigung im nächsten Schritt mit Ihrer persönlichen PIN."
                    : "Confirm the cancellation with your personal PIN in the next step."}
                </p>
              )}

              <div className="flex flex-col-reverse md:flex-row gap-3">
                <button
                  type="button"
                  data-testid="service-package-cancel-abort"
                  onClick={() => !submitting && onClose?.()}
                  disabled={submitting}
                  className="flex-1 border border-navy/25 text-navy px-5 py-3 text-[11.5px] tracking-[0.18em] uppercase hover:bg-navy/5 transition-colors disabled:opacity-50"
                >
                  {de ? "Zurück" : "Back"}
                </button>
                <button
                  type="button"
                  data-testid="service-package-cancel-submit"
                  onClick={onSubmit}
                  disabled={submitting || !endDate}
                  className="flex-1 text-ivory px-5 py-3 text-[11.5px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity disabled:opacity-50"
                  style={{ background: STATUS_COLORS.red }}
                >
                  {submitting ? (de ? "Wird gekündigt …" : "Cancelling …") : (de ? "Verbindlich kündigen" : "Cancel bindingly")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {!isAdmin && (
        <CustomerPinPromptDialog
          open={pinOpen}
          mode="confirm"
          de={de}
          title={de ? "Kündigung signieren" : "Sign cancellation"}
          description={de
            ? "Bitte geben Sie Ihre persönliche 4-stellige PIN ein, um die Kündigung digital zu signieren."
            : "Please enter your personal 4-digit PIN to sign this cancellation."}
          submitLabel={de ? "Verbindlich kündigen" : "Cancel bindingly"}
          onClose={() => !submitting && setPinOpen(false)}
          onConfirm={doCancel}
        />
      )}
    </>,
    document.body
  );
}
